import React, { useState, memo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Key, Laptop, Globe, Layers, Database, Plus, RefreshCw, ChevronDown, ChevronRight, PlusCircle, Undo2 } from 'lucide-react';
import { CISA_PILLARS } from '../../config/cisaMaturityModel';
import { SAMPLE_PRODUCTS, PRODUCT_CATEGORIES } from './PillarTemplate';

/**
 * Pillar icon mapping
 */
const PILLAR_ICONS = {
  IDENTITY: Key,
  DEVICES: Laptop,
  NETWORKS: Globe,
  APPLICATIONS: Layers,
  DATA: Database
};

/**
 * Product item with click-to-add AND drag support
 */
const ProductItem = memo(function ProductItem({ product, pillarCode, onAdd }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${pillarCode}-${product.name}-${product.product}`,
    data: {
      type: 'palette-product',
      product,
      pillarCode
    }
  });

  const handleClick = (e) => {
    e.stopPropagation();
    onAdd?.(product);
  };

  return (
    <div
      ref={setNodeRef}
      className={`arch-sidebar__product ${isDragging ? 'arch-sidebar__product--dragging' : ''}`}
    >
      <div
        className="arch-sidebar__product-drag"
        {...attributes}
        {...listeners}
      >
        <span className="arch-sidebar__product-name">{product.name}</span>
        <span className="arch-sidebar__product-type">{product.product}</span>
      </div>
      <button
        className="arch-sidebar__product-add"
        onClick={handleClick}
        title={`Add ${product.product} to canvas`}
      >
        <PlusCircle size={16} />
      </button>
    </div>
  );
});

/**
 * ArchitectureSidebar Component
 *
 * Displays pillar tabs and a node palette for adding new products.
 */
const ArchitectureSidebar = memo(function ArchitectureSidebar({
  selectedPillar,
  onSelectPillar,
  onResetPillar,
  onAddNode,
  onUndo,
  canUndo = false,
  existingNodes = [],
  readOnly = false
}) {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [customProduct, setCustomProduct] = useState('');

  // Get products for current pillar
  const products = SAMPLE_PRODUCTS[selectedPillar] || [];
  const categories = PRODUCT_CATEGORIES[selectedPillar] || [];

  // Group products by category
  const productsByCategory = categories.reduce((acc, cat) => {
    acc[cat.key] = products.filter(p => p.category === cat.key);
    return acc;
  }, {});

  /**
   * Calculate a good position for a new node (avoid overlap)
   */
  const getNextPosition = () => {
    const baseX = 350;
    const baseY = 200;
    const offsetStep = 30;

    // Find a position that doesn't overlap existing nodes
    let x = baseX;
    let y = baseY;
    let attempts = 0;

    while (attempts < 20) {
      const hasOverlap = existingNodes.some(node =>
        Math.abs(node.x - x) < 100 && Math.abs(node.y - y) < 60
      );

      if (!hasOverlap) break;

      x = baseX + (attempts % 4) * offsetStep;
      y = baseY + Math.floor(attempts / 4) * offsetStep;
      attempts++;
    }

    return { x, y };
  };

  /**
   * Handle clicking the + button on a product
   */
  const handleAddProduct = (product) => {
    if (readOnly) return;

    const pos = getNextPosition();
    onAddNode?.({
      label: product.product,
      vendorName: product.name,
      productName: product.product,
      category: product.category,
      x: pos.x,
      y: pos.y
    });
  };

  const handleAddCustomProduct = () => {
    if (!customProduct.trim() || readOnly) return;

    onAddNode?.({
      label: customProduct.trim(),
      vendorName: 'Custom',
      productName: customProduct.trim(),
      category: 'custom',
      x: 400,
      y: 300
    });

    setCustomProduct('');
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategory(prev => prev === categoryKey ? null : categoryKey);
  };

  return (
    <div className="arch-sidebar">
      {/* Pillar tabs */}
      <div className="arch-sidebar__pillars">
        {CISA_PILLARS.map(pillar => {
          const Icon = PILLAR_ICONS[pillar.code] || Layers;
          const isActive = selectedPillar === pillar.code;

          return (
            <button
              key={pillar.code}
              className={`arch-sidebar__pillar-tab ${isActive ? 'arch-sidebar__pillar-tab--active' : ''}`}
              onClick={() => onSelectPillar?.(pillar.code)}
              title={pillar.name}
              style={{
                '--pillar-color': pillar.color
              }}
            >
              <Icon size={16} />
              <span className="arch-sidebar__pillar-name">{pillar.name.split(' ')[0]}</span>
            </button>
          );
        })}
      </div>

      {/* Actions - Undo and Reset */}
      <div className="arch-sidebar__actions">
        <div className="arch-sidebar__actions-row">
          <button
            className="arch-sidebar__undo-btn"
            onClick={() => onUndo?.()}
            title="Undo last deletion"
            disabled={!canUndo || readOnly}
          >
            <Undo2 size={14} />
            Undo
          </button>
          <button
            className="arch-sidebar__reset-btn"
            onClick={() => onResetPillar?.(selectedPillar)}
            title="Reset to default template"
            disabled={readOnly}
          >
            <RefreshCw size={14} />
            Reset
          </button>
        </div>
      </div>

      {/* Product palette - only show on desktop and when not readonly */}
      {!readOnly && (
        <div className="arch-sidebar__palette">
          <div className="arch-sidebar__palette-header">
            <span>Add Products</span>
          </div>

          {/* Categorized products */}
          <div className="arch-sidebar__categories">
            {categories.map(category => {
              const categoryProducts = productsByCategory[category.key] || [];
              if (categoryProducts.length === 0) return null;

              const isExpanded = expandedCategory === category.key;

              return (
                <div key={category.key} className="arch-sidebar__category">
                  <button
                    className="arch-sidebar__category-header"
                    onClick={() => toggleCategory(category.key)}
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span>{category.label}</span>
                    <span className="arch-sidebar__category-count">{categoryProducts.length}</span>
                  </button>

                  {isExpanded && (
                    <div className="arch-sidebar__category-products">
                      {categoryProducts.map(product => (
                        <ProductItem
                          key={`${product.name}-${product.product}`}
                          product={product}
                          pillarCode={selectedPillar}
                          onAdd={handleAddProduct}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Custom product input */}
          <div className="arch-sidebar__custom">
            <input
              type="text"
              className="arch-sidebar__custom-input"
              placeholder="Custom product name..."
              value={customProduct}
              onChange={(e) => setCustomProduct(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddCustomProduct();
              }}
            />
            <button
              className="arch-sidebar__custom-btn"
              onClick={handleAddCustomProduct}
              disabled={!customProduct.trim()}
              title="Add custom product"
            >
              <Plus size={14} />
            </button>
          </div>

          <p className="arch-sidebar__hint">
            Drag products onto canvas. Drag from anchor dots (●) to connect nodes.
          </p>
        </div>
      )}
    </div>
  );
});

export default ArchitectureSidebar;
